import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, CheckCircle, Clock, PieChart, Target, TrendingUp, Zap } from 'lucide-react';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import {
  CountUp,
  EASE_OUT,
  Reveal,
  SpotlightCard,
  Stagger,
  StaggerItem,
} from '@/components/landing/motion-primitives';
import {
  CtaSection,
  MetaDot,
  PageHero,
  Section,
  SectionHeading,
} from '@/components/landing/page-kit';

/**
 * Article de fond : méthode 50/30/20.
 * Même grammaire visuelle que la landing ; les trois catégories gardent leurs
 * couleurs propres, seule entorse à l'accent unique — elles servent de légende.
 */

const CATEGORY_COLORS = {
  needs: '#16c47f',
  wants: '#f59e0b',
  savings: '#3b82f6',
} as const;

export default function Method503020Page() {
  const lastUpdated = '22 avril 2026';
  const readTime = 8;

  const methodSteps = [
    {
      percentage: 50,
      label: 'Besoins',
      description: 'Dépenses essentielles',
      details: [
        'Loyer ou hypothèque',
        'Alimentation',
        'Assurances',
        'Transport',
        'Services publics',
        'Autres nécessités',
      ],
      color: CATEGORY_COLORS.needs,
      icon: PieChart,
    },
    {
      percentage: 30,
      label: 'Envies',
      description: 'Plaisirs et loisirs',
      details: [
        'Sorties au restaurant',
        'Loisirs et hobbies',
        'Divertissement',
        'Shopping personnel',
        'Vacances',
        'Abonnements premium',
      ],
      color: CATEGORY_COLORS.wants,
      icon: Zap,
    },
    {
      percentage: 20,
      label: 'Épargne',
      description: 'Avenir et sécurité',
      details: [
        "Fonds d'urgence",
        'Retraite',
        'Investissements',
        'Remboursement de dettes',
        'Économies à long terme',
        'Buts financiers',
      ],
      color: CATEGORY_COLORS.savings,
      icon: Target,
    },
  ];

  const benefits = [
    {
      title: 'Simple à comprendre',
      description: 'Une règle facile à retenir et à mettre en pratique pour tous les niveaux.',
      icon: CheckCircle,
    },
    {
      title: 'Flexible et adaptable',
      description: 'Ajustez les pourcentages selon votre situation financière personnelle.',
      icon: TrendingUp,
    },
    {
      title: 'Équilibre maintenu',
      description: "Permet d'épargner tout en profitant des plaisirs de la vie.",
      icon: Target,
    },
    {
      title: 'Sécurité financière',
      description: 'Vous construisez progressivement un coussin de sécurité.',
      icon: Zap,
    },
  ];

  const example = { income: 3000, needs: 1500, wants: 900, savings: 600 };

  const breakdown = [
    { label: 'Besoins (50%)', amount: example.needs, color: CATEGORY_COLORS.needs },
    { label: 'Envies (30%)', amount: example.wants, color: CATEGORY_COLORS.wants },
    { label: 'Épargne (20%)', amount: example.savings, color: CATEGORY_COLORS.savings },
  ];

  const tips = [
    {
      title: 'Soyez flexible',
      text: 'Les pourcentages sont une ligne directrice, pas des règles strictes. Ajustez-les selon vos besoins.',
    },
    {
      title: 'Classifiez correctement',
      text: 'Soyez honnête sur ce qui est un besoin et ce qui est une envie pour que le budget fonctionne.',
    },
    {
      title: 'Suivez régulièrement',
      text: 'Vérifiez votre répartition chaque mois pour rester sur la bonne voie.',
    },
    {
      title: 'Utilisez clevia',
      text: 'Notre app facilite le suivi automatique de vos catégories de dépenses.',
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <LandingHeader />

      <main>
        <PageHero
          align="left"
          width="narrow"
          eyebrow="Guide budget"
          title="Méthode"
          highlight="50/30/20"
          subtitle="Découvrez la méthode éprouvée pour gérer votre budget de façon simple et efficace."
          meta={
            <>
              <Link
                to="/help"
                className="transition-colors hover:text-brand-700 dark:hover:text-brand-400"
              >
                Centre d'aide
              </Link>
              <MetaDot />
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                Mis à jour le {lastUpdated}
              </span>
              <MetaDot />
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                {readTime} min de lecture
              </span>
            </>
          }
        />

        {/* ── Introduction ───────────────────────────────────────── */}
        <Section width="narrow" className="pt-0 lg:pt-0">
          <Stagger stagger={0.08}>
            <StaggerItem>
              <p className="text-pretty text-lg leading-relaxed text-muted-foreground">
                La méthode 50/30/20 est une règle de gestion budgétaire simple et efficace qui vous
                aide à répartir vos revenus de manière équilibrée. Cette approche éprouvée,
                popularisée par l'experte financière Elizabeth Warren, offre un cadre flexible pour
                atteindre vos objectifs financiers tout en profitant de la vie.
              </p>
            </StaggerItem>
            <StaggerItem>
              <p className="mt-6 text-pretty text-lg leading-relaxed text-muted-foreground">
                En trois catégories seulement, vous pouvez créer un budget durable et adapté à votre
                situation personnelle.
              </p>
            </StaggerItem>
          </Stagger>
        </Section>

        {/* ── Les trois piliers ──────────────────────────────────── */}
        <Section tone="muted">
          <SectionHeading
            eyebrow="La règle"
            title="Les trois piliers"
            subtitle="Répartissez votre revenu net selon ces trois catégories."
            align="center"
          />

          <Stagger className="mt-16 grid gap-4 md:grid-cols-3" stagger={0.09}>
            {methodSteps.map((step) => (
              <StaggerItem key={step.label}>
                <SpotlightCard className="flex h-full flex-col rounded-xl border border-border bg-card p-7 transition-shadow duration-300 hover:shadow-lg">
                  <div className="flex items-baseline justify-between">
                    <span
                      className="text-4xl font-semibold tracking-tight tabular-nums"
                      style={{ color: step.color }}
                    >
                      <CountUp to={step.percentage} />%
                    </span>
                    <step.icon className="h-5 w-5" style={{ color: step.color }} />
                  </div>

                  <h3 className="mt-6 text-lg font-medium">{step.label}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{step.description}</p>

                  <ul className="mt-6 space-y-2 border-t border-border pt-6">
                    {step.details.map((detail) => (
                      <li
                        key={detail}
                        className="flex items-start gap-2.5 text-sm text-muted-foreground"
                      >
                        <span
                          className="mt-[7px] h-1 w-1 shrink-0 rounded-full"
                          style={{ backgroundColor: step.color }}
                          aria-hidden="true"
                        />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </SpotlightCard>
              </StaggerItem>
            ))}
          </Stagger>

          {/* Exemple chiffré */}
          <Reveal className="mx-auto mt-16 max-w-2xl">
            <div className="rounded-xl border border-border bg-card p-8">
              <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                Exemple
              </p>
              <h3 className="mt-4 text-balance text-xl font-medium">
                Un revenu net de{' '}
                <span className="tabular-nums">
                  <CountUp to={example.income} />
                </span>{' '}
                €
              </h3>

              <div className="mt-8 space-y-5">
                {breakdown.map((item, i) => (
                  <div key={item.label}>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="font-medium">{item.label}</span>
                      <span className="tabular-nums" style={{ color: item.color }}>
                        {item.amount} €
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                      <motion.div
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: i * 0.1, ease: EASE_OUT }}
                        style={{
                          width: `${(item.amount / example.income) * 100}%`,
                          backgroundColor: item.color,
                          transformOrigin: 'left',
                        }}
                        className="h-full rounded-full"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </Section>

        {/* ── Avantages ──────────────────────────────────────────── */}
        <Section>
          <SectionHeading
            eyebrow="Pourquoi ça marche"
            title="Avantages de cette méthode"
            subtitle="Pourquoi 50/30/20 fonctionne pour beaucoup de gens."
          />

          <Stagger className="mt-16 grid gap-4 sm:grid-cols-2" stagger={0.07}>
            {benefits.map((benefit) => (
              <StaggerItem key={benefit.title}>
                <SpotlightCard className="h-full rounded-xl border border-border bg-card p-7 transition-shadow duration-300 hover:shadow-lg">
                  <benefit.icon className="h-5 w-5 text-primary" />
                  <h3 className="mt-5 text-base font-medium">{benefit.title}</h3>
                  <p className="mt-2 text-pretty text-sm leading-relaxed text-muted-foreground">
                    {benefit.description}
                  </p>
                </SpotlightCard>
              </StaggerItem>
            ))}
          </Stagger>
        </Section>

        {/* ── Conseils ───────────────────────────────────────────── */}
        <Section tone="dark">
          <div className="grid gap-14 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20">
            <SectionHeading
              eyebrow="En pratique"
              title="Conseils pour réussir"
              subtitle="Quatre réflexes qui font tenir la règle dans la durée."
              tone="dark"
            />

            <Stagger className="grid gap-x-10 gap-y-10 sm:grid-cols-2" stagger={0.09}>
              {tips.map((tip) => (
                <StaggerItem key={tip.title}>
                  <div className="border-t border-white/10 pt-6 transition-colors duration-300 hover:border-[#16c47f]/50">
                    <h3 className="text-base font-medium text-white">{tip.title}</h3>
                    <p className="mt-2 text-pretty text-sm leading-relaxed text-gray-400">
                      {tip.text}
                    </p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </Section>

        {/* ── CTA final ──────────────────────────────────────────── */}
        <CtaSection
          title="Commencez à appliquer la méthode 50/30/20"
          subtitle="Utilisez clevia pour automatiser le suivi de vos dépenses et appliquer cette méthode sans effort."
          action={{ label: 'Commencer maintenant', to: '/register' }}
        />
      </main>

      <LandingFooter />
    </div>
  );
}
